import type { TFunction } from 'i18next'
import type { MediaChapter, MediaEpisode, MediaShowModule } from '@/lib/sdk-official/resources/media-shows'

/** Where the module's screens keep their texts. */
export const MEDIA_NS = 'media'

/** "1:02:03" or "12:05": the way a player writes a time. */
export const formatClock = (total: number | null | undefined): string => {
  const seconds = Math.max(0, Math.floor(Number(total) || 0))
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

/** "12:05", "1:02:03" or "75" (seconds) into seconds; `null` when it is not a time. */
export const parseClock = (text: string): number | null => {
  const clean = text.trim()
  if (!/^\d{1,3}(:\d{1,2}){0,2}$/.test(clean)) return null
  return clean.split(':').map(Number).reduce((total, part) => total * 60 + part, 0)
}

/** "T2 · E5" (season and number), only the parts the episode has. */
export const episodeCode = (episode: Pick<MediaEpisode, 'season' | 'number'>, t: TFunction): string =>
  [episode.season ? t('media:episode.seasonShort', { n: episode.season }) : '', episode.number !== null && episode.number !== undefined ? t('media:episode.numberShort', { n: episode.number }) : '']
    .filter(Boolean).join(' · ')

/** The chapter playing at `seconds` (the last one that started). */
export const chapterAt = (chapters: MediaChapter[], seconds: number): number => {
  let index = -1
  chapters.forEach((chapter, i) => { if (chapter.startSeconds <= seconds) index = i })
  return index
}

/** The episode page inside its space. */
export const episodePath = (sectionSlug: string, spaceSlug: string, episodeId: string, edit = false) =>
  `/${sectionSlug}/${spaceSlug}/${episodeId}${edit ? '?edit=1' : ''}`

/** A new episode of `showId`: the editor on the space's `new` address. */
export const newEpisodePath = (sectionSlug: string, spaceSlug: string, showId: string) =>
  `/${sectionSlug}/${spaceSlug}/new?show=${encodeURIComponent(showId)}`

/** The query of the address on screen: the route's `currentPath`, or the browser's on a first load (which keeps only the path). */
export const queryOf = (path: string) =>
  new URLSearchParams(path.includes('?') ? path.split('?')[1].split('#')[0] : typeof window !== 'undefined' && window.location.pathname === path.split('#')[0] ? window.location.search : '')

type ApiError = { message?: string; response?: { status?: number; data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } } } | null

export const statusOf = (error: unknown): number | undefined => (error as ApiError)?.response?.status

/** The API's message (already translated), else the fallback. */
export const messageOf = (error: unknown, fallback: string): string => {
  const err = error as ApiError
  return err?.response?.data?.message || err?.response?.data?.errors?.[0]?.message || fallback
}

/** Per-field messages of a 400, by `param`. */
export const fieldErrorsOf = (error: unknown): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const e of (error as ApiError)?.response?.data?.errors ?? []) if (e.param && e.message && !out[e.param]) out[e.param] = e.message
  return out
}

/** The text of rich HTML, to tell an empty editor from one with words. */
export const hasText = (html: string | null | undefined) => Boolean(String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim())

/** Podcast plays audio first; Vídeos plays video first. */
export const preferredKind = (module: MediaShowModule) => (module === 'videos' ? 'video' : 'audio')

/** An id for one play session of the player (`sessionId` of the progress route). */
export const newSessionId = () => `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`

/** Percent text of a 0..1 share, or "–" when unknown. */
export const percentOf = (share: number | null | undefined, locale: string) =>
  share === null || share === undefined ? '–' : new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(share)
